import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatTime, getPhaseTypeLabel, getPhaseTypeColor, getTimeDiff } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { GripVertical, MoreVertical, Edit, Trash2, Copy, Mic, Radio, MessageSquare, FileText, Play, Music, GitBranch, CornerDownRight, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ResourcePreviewModal from './ResourcePreviewModal';

// Speaker avatar with hover card showing photo and details
const SpeakerAvatar = ({ speaker, microphone }) => {
  if (!speaker) return null;
  
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity min-w-0">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium overflow-hidden flex-shrink-0">
            {speaker.avatar_url ? (
              <img src={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" />
            ) : (
              speaker.initials
            )}
          </div>
          <span className="text-sm truncate">{speaker.name.split(' ')[0]}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="top">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            {speaker.avatar_url ? (
              <img src={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-muted-foreground">{speaker.initials}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold">{speaker.name}</h4>
            {speaker.role && <p className="text-sm text-muted-foreground">{speaker.role}</p>}
            {microphone && (
              <div className="flex items-center gap-1 mt-2 text-xs">
                <Mic className="w-3 h-3 text-red-500" />
                <span className="text-red-500 font-medium">{microphone.name}</span>
                {microphone.channel && <span className="text-muted-foreground">({microphone.channel})</span>}
              </div>
            )}
            {!microphone && (
              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                <Radio className="w-3 h-3" />
                <span>Sin micrófono asignado</span>
              </div>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export const PhaseRow = ({
  phase,
  index,
  isActive,
  isNext,
  phaseElapsed,
  phaseStartTime = null,
  speakers,
  microphones = [],
  screens = [],
  onEdit,
  onDelete,
  onDuplicate,
  onAddSubPhase = null,
  isSubPhase = false,
  onSpeakersChange,
  commentCount = 0,
  onCommentClick,
  visibleCols = { onStage: true, mic: true, hora: true, est: true, real: true, diff: true },
  gridTemplate = '40px minmax(160px,1fr) 140px 100px 64px 64px 64px 44px 44px',
}) => {
  const [preview, setPreview] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get speakers for this phase (supports multiple)
  const phaseSpeakers = (phase.speaker_ids || [])
    .map(id => speakers.find(s => s.id === id))
    .filter(Boolean);
  
  // Legacy support for single speaker_id
  if (phaseSpeakers.length === 0 && phase.speaker_id) {
    const legacySpeaker = speakers.find(s => s.id === phase.speaker_id);
    if (legacySpeaker) phaseSpeakers.push(legacySpeaker);
  }

  // Get microphone for each speaker
  const getSpeakerMic = (speakerId) => {
    return microphones.find(m => m.assigned_to === speakerId);
  };

  const speakerNames = phaseSpeakers.map(s => s.name);
  const diff = getTimeDiff(phase.duration_seconds, phaseElapsed);
  const progress = Math.min(100, (phaseElapsed / phase.duration_seconds) * 100);

  const getStatusClass = () => {
    if (phase.status === 'completed') return 'bg-green-500';
    if (isActive) return 'bg-red-500 animate-pulse';
    if (isNext) return 'bg-green-500/50';
    return 'bg-zinc-600';
  };

  const getRowClass = () => {
    if (isActive) return 'phase-active';
    if (isNext) return 'phase-next';
    return '';
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, gridTemplateColumns: gridTemplate }}
      className={`phase-row relative grid gap-2 items-center px-3 py-3 border-b border-border/40 ${getRowClass()}`}
      data-testid={`phase-row-${phase.id}`}
    >
      {/* Status strip */}
      <div className={`status-strip ${getStatusClass()}`} style={{ backgroundColor: isActive || isNext ? undefined : phase.color }} />

      {/* Drag handle */}
      <div
        {...(isSubPhase ? {} : { ...attributes, ...listeners })}
        className={`drag-handle flex items-center justify-center ${isSubPhase ? 'opacity-0 pointer-events-none' : 'cursor-grab active:cursor-grabbing'}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>

      {/* Name & Description (type badge inline) */}
      <div className={`min-w-0 ${isSubPhase ? 'pl-5 border-l-2 border-muted ml-1' : ''}`}>
        {isSubPhase && <CornerDownRight className="w-3 h-3 text-muted-foreground mb-0.5 inline-block mr-1 opacity-50" />}
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          {isActive && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 uppercase tracking-widest animate-pulse-live">
              En vivo
            </Badge>
          )}
          {isNext && !isActive && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 uppercase tracking-widest border-green-500/50 text-green-500">
              Siguiente
            </Badge>
          )}
          <Badge
            variant="outline"
            className="text-[10px] uppercase tracking-widest"
            style={{ borderColor: getPhaseTypeColor(phase.phase_type), color: getPhaseTypeColor(phase.phase_type) }}
          >
            {getPhaseTypeLabel(phase.phase_type)}
          </Badge>
        </div>
        <div className="font-medium truncate">{phase.name}</div>
        {phase.description && (
          <div className="text-xs text-muted-foreground truncate">{phase.description}</div>
        )}
        {/* Progress bar */}
        {(isActive || phase.status === 'completed') && (
          <div className="phase-progress mt-2">
            <div
              className={`phase-progress-bar ${progress > 100 ? 'overtime' : progress > 80 ? 'warning' : 'normal'}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Info / Notes */}
      <div className="min-w-0">
        {phase.notes ? (
          <button
            type="button"
            onClick={() => setNotesOpen(true)}
            className="w-full text-left group flex items-start gap-1 hover:text-foreground transition-colors"
          >
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-xs text-muted-foreground group-hover:text-foreground line-clamp-2 leading-relaxed">
              {phase.notes}
            </span>
          </button>
        ) : (
          <span className="text-muted-foreground opacity-30 text-xs">—</span>
        )}
      </div>

      {/* Notes modal */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              Info — {phase.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
            {phase.notes}
          </div>
        </DialogContent>
      </Dialog>

      {/* On Stage - Multiple speakers */}
      {visibleCols.onStage && (
        <div className="flex flex-col gap-1 min-w-0">
          {phaseSpeakers.length > 0 ? (
            phaseSpeakers.map(speaker => (
              <SpeakerAvatar
                key={speaker.id}
                speaker={speaker}
                microphone={getSpeakerMic(speaker.id)}
              />
            ))
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </div>
      )}

      {/* Microphones */}
      {visibleCols.mic && (
        <div className="text-sm min-w-0">
          {phaseSpeakers.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {phaseSpeakers.map(speaker => {
                const mic = getSpeakerMic(speaker.id);
                return mic ? (
                  <div key={speaker.id} className="flex items-center gap-1 text-xs">
                    <Mic className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <span className="truncate text-red-500">{mic.name}</span>
                  </div>
                ) : (
                  <div key={speaker.id} className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Radio className="w-3 h-3 text-green-500 flex-shrink-0" />
                    <span className="text-green-500">libre</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      )}

      {/* Recursos — dynamic columns */}
      {screens.map(screen => {
        const val = phase.screen_values?.[screen.id];
        const contentType = val?.content_type || (typeof val === 'string' && val ? 'image' : null);
        const contentUrl = val?.content_url || (typeof val === 'string' ? val : '');
        const openPreview = (e) => {
          e.preventDefault();
          setPreview({ content_type: contentType, content_url: contentUrl, name: screen.name });
        };
        return (
          <div key={screen.id} className="flex items-center justify-center">
            {contentUrl ? (
              contentType === 'image' ? (
                <button type="button" onClick={openPreview} className="hover:opacity-80 transition-opacity" title={screen.name}>
                  <img
                    src={contentUrl}
                    alt={screen.name}
                    className="h-10 w-16 object-cover rounded border border-border"
                  />
                </button>
              ) : contentType === 'video' ? (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <Play className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Video</span>
                </button>
              ) : contentType === 'audio' ? (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <Music className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Audio</span>
                </button>
              ) : (
                <button type="button" onClick={openPreview} className="flex items-center gap-1 text-primary hover:opacity-80 transition-opacity">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs truncate max-w-[60px]">Doc</span>
                </button>
              )
            ) : (
              <span className="text-muted-foreground text-sm">—</span>
            )}
          </div>
        );
      })}

      {/* Clock start time */}
      {visibleCols.hora && (
        <div className="text-right font-mono text-sm tabular-nums">
          {phaseStartTime
            ? phaseStartTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })
            : '—'}
        </div>
      )}

      {/* Estimated time */}
      {visibleCols.est && (
        <div className="text-right font-mono text-sm tabular-nums">
          {formatTime(phase.duration_seconds)}
        </div>
      )}

      {/* Actual time */}
      {visibleCols.real && (
        <div className="text-right font-mono text-sm tabular-nums">
          {isActive || phase.status === 'completed' ? formatTime(phaseElapsed) : '—'}
        </div>
      )}

      {/* Difference */}
      {visibleCols.diff && (
        <div className={`text-right font-mono text-sm tabular-nums ${
          diff.status === 'overtime' ? 'text-red-500' :
          diff.status === 'under' ? 'text-green-500' :
          'text-muted-foreground'
        }`}>
          {isActive || phase.status === 'completed' ? diff.label : '—'}
        </div>
      )}

      {/* Comment badge */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 relative ${commentCount > 0 ? 'text-primary' : 'text-muted-foreground'}`}
          onClick={() => onCommentClick && onCommentClick(phase.id)}
          title="Comentarios del cliente"
        >
          <MessageSquare className="w-4 h-4" />
          {commentCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-[9px] flex items-center justify-center text-primary-foreground font-bold">
              {commentCount > 9 ? '9+' : commentCount}
            </span>
          )}
        </Button>
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="w-4 h-4 mr-2" />
              Duplicar
            </DropdownMenuItem>
            {!isSubPhase && onAddSubPhase && (
              <DropdownMenuItem onClick={onAddSubPhase}>
                <GitBranch className="w-4 h-4 mr-2" />
                Añadir subfase
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Resource preview modal (portal) */}
      <ResourcePreviewModal resource={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default PhaseRow;
